import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Sparkles, Wallet as WalletIcon, CreditCard, Check, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { useWallet, fmtEuro } from "@/lib/wallet";

// Tier visuals — intentionally minimalist (no gradients, no "AD" sticker).
// Each tier shows a tiny dot icon in the marketplace card.
const TIER_META = {
  bronze: { dot: "#B45309", ring: "border-[#B45309]/40", chip: "bg-[#1a1208] text-[#D97706]" },
  silver: { dot: "#9CA3AF", ring: "border-[#9CA3AF]/40", chip: "bg-[#17191c] text-[#D1D5DB]" },
  gold:   { dot: "#E7B008", ring: "border-[#E7B008]/50", chip: "bg-[#1a1507] text-[#F5C518]" },
};

function TierCard({ tier, cfg, selected, onSelect }) {
  const meta = TIER_META[tier];
  return (
    <button
      type="button"
      data-testid={`boost-tier-${tier}`}
      onClick={() => onSelect(tier)}
      className={`relative text-left w-full rounded-xl border p-3 transition-all ${
        selected
          ? `${meta.ring} bg-[#1a1a1a] shadow-[0_0_0_1px_rgba(255,255,255,0.04)]`
          : "border-[#27272A] bg-[#141414] hover:border-[#3F3F46]"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: meta.dot }} />
          <span className="text-sm font-semibold text-[#F3F4F6]">{cfg.label}</span>
        </div>
        {selected && <Check className="h-4 w-4 text-[#F3F4F6]" />}
      </div>
      <div className="mt-1.5 flex items-baseline gap-1">
        <span className="text-lg font-semibold text-[#F3F4F6]">{fmtEuro(cfg.price_cents)}</span>
        <span className="text-[11px] text-[#9CA3AF]">· {cfg.days === 1 ? "1 day" : cfg.days < 30 ? `${cfg.days} days` : "1 month"}</span>
      </div>
    </button>
  );
}

export default function BoostDialog({ open, onOpenChange, product, onBoosted }) {
  // `product` must have at least { product_id, name, boost? }
  const wallet = useWallet();
  const [tiers, setTiers] = useState([]);
  const [selected, setSelected] = useState("silver");
  const [method, setMethod] = useState("wallet");
  const [busy, setBusy] = useState(false);
  const [currentBoost, setCurrentBoost] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/boost/tiers");
        setTiers(r.data || []);
      } catch (e) {
        toast.error("Could not load boost tiers");
      }
      if (product?.product_id) {
        try {
          const r = await api.get(`/products/${product.product_id}/boost`);
          setCurrentBoost(r.data?.boost || null);
        } catch { /* ignore */ }
      }
    })();
  }, [open, product?.product_id]);

  if (!product) return null;

  const selectedCfg = tiers.find((t) => t.tier === selected);
  const balance = wallet?.balance_cents || 0;
  const walletEnough = selectedCfg ? balance >= selectedCfg.price_cents : false;

  const boost = async () => {
    if (!selectedCfg || busy) return;
    setBusy(true);
    try {
      if (method === "wallet") {
        if (!walletEnough) {
          toast.error("Not enough wallet balance — top up or pay by card.");
          setBusy(false);
          return;
        }
        const r = await api.post(`/products/${product.product_id}/boost`, {
          tier: selected,
          method: "wallet",
        });
        toast.success(`Boost activated: ${selectedCfg.label}`);
        window.dispatchEvent(new Event("notelosh:wallet-changed"));
        onBoosted?.(r.data?.boost);
        onOpenChange(false);
      } else {
        const r = await api.post(`/products/${product.product_id}/boost`, {
          tier: selected,
          method: "stripe",
          origin_url: window.location.origin,
        });
        if (r.data?.url) {
          window.location.href = r.data.url;
        } else {
          toast.error("Could not start checkout");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Boost failed");
      setBusy(false);
    }
  };

  const currentTier = currentBoost?.tier;
  const currentExpiry = currentBoost?.expires_at
    ? new Date(currentBoost.expires_at).toLocaleString()
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="boost-dialog"
        className="bg-[#121212] border-[#27272A] text-[#F3F4F6] max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Rocket className="h-4 w-4 text-[#F5C518]" />
            Promote this note
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-xs text-[#9CA3AF] leading-relaxed">
            Pay a small fee and we'll quietly surface <b className="text-[#F3F4F6]">{product.name}</b> inside relevant feeds and searches. No big "AD" sticker — just a subtle tier dot.
          </p>

          {currentBoost && (
            <div className="rounded-lg border border-[#27272A] bg-[#1a1a1a] px-3 py-2 text-xs text-[#D1D5DB]">
              <span className="text-[#9CA3AF]">Currently </span>
              <span className="font-medium capitalize">{currentTier}</span>
              <span className="text-[#9CA3AF]"> · ends </span>
              <span>{currentExpiry}</span>
              <div className="text-[11px] text-[#6B7280] mt-0.5">Buying again extends the clock. A higher tier upgrades the badge.</div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            {tiers.map((cfg) => (
              <TierCard
                key={cfg.tier}
                tier={cfg.tier}
                cfg={cfg}
                selected={selected === cfg.tier}
                onSelect={setSelected}
              />
            ))}
          </div>

          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">Pay with</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="boost-method-wallet"
                onClick={() => setMethod("wallet")}
                className={`rounded-xl border p-3 text-left transition ${
                  method === "wallet"
                    ? "border-[#3B82F6]/50 bg-[#101827]"
                    : "border-[#27272A] bg-[#141414] hover:border-[#3F3F46]"
                }`}
              >
                <div className="flex items-center gap-2 text-sm">
                  <WalletIcon className="h-4 w-4 text-[#3B82F6]" />
                  Wallet
                </div>
                <div className={`text-[11px] mt-0.5 ${walletEnough ? "text-[#9CA3AF]" : "text-[#F87171]"}`}>
                  Balance {fmtEuro(balance)}
                </div>
              </button>
              <button
                type="button"
                data-testid="boost-method-stripe"
                onClick={() => setMethod("stripe")}
                className={`rounded-xl border p-3 text-left transition ${
                  method === "stripe"
                    ? "border-[#3B82F6]/50 bg-[#101827]"
                    : "border-[#27272A] bg-[#141414] hover:border-[#3F3F46]"
                }`}
              >
                <div className="flex items-center gap-2 text-sm">
                  <CreditCard className="h-4 w-4 text-[#3B82F6]" />
                  Card
                </div>
                <div className="text-[11px] text-[#9CA3AF] mt-0.5">Stripe Checkout</div>
              </button>
            </div>
          </div>

          <Button
            data-testid="boost-submit-btn"
            onClick={boost}
            disabled={busy || !selectedCfg || (method === "wallet" && !walletEnough)}
            className="w-full h-11 rounded-full bg-[#F5C518] hover:bg-[#E6B800] text-black font-semibold gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {selectedCfg
              ? `Boost · ${selectedCfg.label} · ${fmtEuro(selectedCfg.price_cents)}`
              : "Boost"}
          </Button>
          <p className="text-[11px] text-[#6B7280] text-center">
            Boost fees go to NoteLosh and are non-refundable.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
